/**
 * 計算フィールド 計算エンジン（#3145）の静的検証。
 * 構文・未知参照・スコープ違反・自己参照・正規表現リテラル・上限をパターン保存前に検証する。
 * ⚠️ functions/src/utils/calculation と main/src/utils/calculation は同一内容。
 *    片方だけ変更しないこと（変更時は両方へ反映し、共通テストで一致を確認する）。
 */
import {
  CALCULATION_REGEX_MAX_LENGTH,
  calculationErrorCode,
  calculationFunction,
  calculationWarningCode,
  calculationWarningMessage,
} from './constants';
import { CalculationFormulaError } from './calculationError';
import { CalculationWarning, ValidateFormulaOptions, ValidateFormulaResult } from './types';
import { AstNode, parseFormula } from './parser';

// フィールドコード自体の形式・一意性の検証はエンジンの責務外。
// main 側の utils/pattern/fieldCode.ts（#3175）が持つ（採番・検証とも）。

/** 破滅的バックトラッキングを起こしやすい「量指定子のネスト」の簡易検知（例: (a+)+ ） */
const NESTED_QUANTIFIER_PATTERN = /\([^()]*[+*}][^()]*\)[+*{]/;

/** 項目参照の妥当性（自己参照 → 参照可否 → 存在有無の順に検証する） */
const validateFieldRef = (fieldCode: string, options: ValidateFormulaOptions): void => {
  if (options.selfFieldCode !== undefined && fieldCode === options.selfFieldCode) {
    throw new CalculationFormulaError(
      calculationErrorCode.circular,
      `自分自身を参照しています: {${fieldCode}}`,
    );
  }
  if (options.referenceableFieldCodes.includes(fieldCode)) {
    return;
  }
  const allFieldCodes = options.allFieldCodes ?? options.referenceableFieldCodes;
  if (allFieldCodes.includes(fieldCode)) {
    throw new CalculationFormulaError(calculationErrorCode.outOfScope, `項目: {${fieldCode}}`);
  }
  throw new CalculationFormulaError(calculationErrorCode.unknownField, `項目: {${fieldCode}}`);
};

/** 正規表現リテラル（REPLACE / MATCH の第2引数）の静的検証 */
const validateRegexLiteral = (pattern: string, warnings: CalculationWarning[]): void => {
  if (pattern.length > CALCULATION_REGEX_MAX_LENGTH) {
    throw new CalculationFormulaError(
      calculationErrorCode.limitExceeded,
      `正規表現は ${CALCULATION_REGEX_MAX_LENGTH} 文字以内で入力してください`,
    );
  }
  try {
    new RegExp(pattern);
  } catch {
    throw new CalculationFormulaError(calculationErrorCode.invalidRegex, `パターン: ${pattern}`);
  }
  if (NESTED_QUANTIFIER_PATTERN.test(pattern)) {
    warnings.push({
      code: calculationWarningCode.dangerousRegex,
      message: calculationWarningMessage[calculationWarningCode.dangerousRegex],
    });
  }
};

const validateNode = (
  node: AstNode,
  options: ValidateFormulaOptions,
  warnings: CalculationWarning[],
): void => {
  switch (node.type) {
    case 'number':
    case 'string':
      return;
    case 'fieldRef':
      validateFieldRef(node.fieldCode, options);
      return;
    case 'unary':
      validateNode(node.operand, options, warnings);
      return;
    case 'binary':
      validateNode(node.left, options, warnings);
      validateNode(node.right, options, warnings);
      return;
    case 'funcCall': {
      node.args.forEach(arg => validateNode(arg, options, warnings));
      const isRegexFunction =
        node.name === calculationFunction.replace || node.name === calculationFunction.match;
      const patternArg = node.args[1];
      if (isRegexFunction && patternArg?.type === 'string') {
        validateRegexLiteral(patternArg.value, warnings);
      }
      return;
    }
  }
};

/**
 * 計算式を静的に検証する（パターン保存前・入力中のチェック用）。
 * フィールド間の循環参照は resolveCalculationOrder 側で検証する
 */
export const validateFormula = (
  formula: string,
  options: ValidateFormulaOptions,
): ValidateFormulaResult => {
  const warnings: CalculationWarning[] = [];
  try {
    const ast = parseFormula(formula);
    validateNode(ast, options, warnings);
    return { isValid: true, error: null, warnings };
  } catch (error) {
    if (error instanceof CalculationFormulaError) {
      return {
        isValid: false,
        error: { code: error.code, message: error.message },
        warnings,
      };
    }
    throw error;
  }
};
